import * as CANNON from 'cannon-es'
import type { BallEntity, LotteryMachineConfig, MachineId } from '../core/types'
import type { FeedTube, MachineLayout } from './MachineLayout'
import { ballId, getBallMaterial } from './PhysicsWorldFactory'

export interface BallBodyBundle {
  entity: BallEntity
  body: CANNON.Body
}

/** 装在顶管闸板上的堆叠高度 */
function rackStackY(tubeBottomY: number, r: number, stack: number): number {
  return tubeBottomY + 0.006 + r + stack * (r * 2.08)
}

/**
 * 装球：先放在顶部落球管闸板上（运动学），开闸前不会掉。
 */
export function createBalls(
  world: CANNON.World,
  machineId: MachineId,
  poolSize: number,
  config: LotteryMachineConfig,
  tubes: FeedTube[],
): BallBodyBundle[] {
  const mat = getBallMaterial(world)
  const bundles: BallBodyBundle[] = []
  const r = config.ballRadius

  for (let n = 1; n <= poolSize; n++) {
    const tube = tubes[(n - 1) % tubes.length]!
    const stack = Math.floor((n - 1) / tubes.length)
    const mass =
      config.ballMass +
      (Math.random() * 2 - 1) * config.ballMassJitter

    const body = new CANNON.Body({
      mass,
      type: CANNON.Body.KINEMATIC,
      material: mat,
      shape: new CANNON.Sphere(r),
      position: new CANNON.Vec3(tube.x, rackStackY(tube.bottomY, r, stack), tube.z),
      linearDamping: 0.01,
      angularDamping: 0.05,
      allowSleep: false,
    })
    body.velocity.set(0, 0, 0)
    body.angularVelocity.set(0, 0, 0)
    world.addBody(body)

    bundles.push({
      entity: {
        id: ballId(machineId, n),
        machineId,
        number: n,
        mass,
        state: 'rack',
      },
      body,
    })
  }
  return bundles
}

/**
 * 开闸落球：球仍在管口位置，改为动力学，仅靠重力自由落下。
 * 不做空中瞬移、不对中、不限速。
 */
export function releaseBallsFromTubes(
  bundles: BallBodyBundle[],
  layout: MachineLayout,
  ballRadius: number,
): void {
  const tubes = layout.feedTubes
  const r = ballRadius

  for (const b of bundles) {
    if (b.entity.state !== 'rack') continue
    const tube = tubes[(b.entity.number - 1) % tubes.length]!
    const stack = Math.floor((b.entity.number - 1) / tubes.length)

    b.body.type = CANNON.Body.DYNAMIC
    b.body.mass = b.entity.mass
    b.body.updateMassProperties()
    // 保持在顶管堆叠位，略微错开减少互卡
    b.body.position.set(
      tube.x + (Math.random() - 0.5) * r * 0.2,
      rackStackY(tube.bottomY, r, stack),
      tube.z + (Math.random() - 0.5) * r * 0.2,
    )
    b.body.velocity.set(0, 0, 0)
    b.body.angularVelocity.set(0, 0, 0)
    b.body.wakeUp()
  }
}

/**
 * 仅调用一次：开闸后仍停在管口上方的球，推到管口下方让重力接管。
 */
export function rescueBallsStillInRack(
  bundles: BallBodyBundle[],
  tubes: FeedTube[],
  ballRadius: number,
): number {
  const r = ballRadius
  let n = 0
  for (const { body, entity } of bundles) {
    if (entity.state !== 'rack' && entity.state !== 'in_chamber') continue
    const tube = tubes[(entity.number - 1) % tubes.length]!
    if (body.position.y < tube.bottomY + r * 0.5) continue

    body.type = CANNON.Body.DYNAMIC
    body.mass = entity.mass
    body.updateMassProperties()
    body.position.set(
      tube.x + (Math.random() - 0.5) * r * 0.4,
      tube.bottomY - r * 1.1,
      tube.z + (Math.random() - 0.5) * r * 0.4,
    )
    body.velocity.set(
      (Math.random() - 0.5) * 0.4,
      -0.5,
      (Math.random() - 0.5) * 0.4,
    )
    body.angularVelocity.set(0, 0, 0)
    body.wakeUp()
    n += 1
  }
  return n
}
